import { StateCreator } from "zustand";
import { produce } from "immer";
import { supabase } from "@/lib/supabase";
import { ArchiveFileType, ArchiveJobGroup } from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../Central";
import { showNotification } from "../Central/selectors";

const JOB_GROUPS_PAGE_SIZE = 30;
const FILES_PAGE_SIZE = 50;
/** Upper bound for "Alle auswählen" and therefore for a single ZIP. Surfaced when hit. */
export const SELECT_ALL_CAP = 1000;
const SEARCH_DEBOUNCE_MS = 300;

/** The "ohne Auftrag" bucket (Sammelrechnungen, Akonto, org uploads) has no resolved_job_id. */
export const NO_JOB_KEY = "none";
/** Scope value for files that belong to no Auftraggeber; no scope at all means every file. */
export const NO_CLIENT_SCOPE = "none";

export type ArchiveSort = "date_desc" | "date_asc" | "name_asc" | "name_desc";
export type ArchiveViewMode = "grouped" | "flat";

export const groupKey = (group: Pick<ArchiveJobGroup, "resolved_job_id">) =>
	group.resolved_job_id === null ? NO_JOB_KEY : String(group.resolved_job_id);

/** Counts are only known for a single client; the organisation-wide scopes have none. */
export interface ArchiveOverview {
	total: number | null;
	minDate: string | null;
	maxDate: string | null;
	years: { year: number; count: number | null }[];
	kinds: { kind: string; count: number }[] | null;
}

interface ArchiveState {
	/** null = every file, NO_CLIENT_SCOPE = files without Auftraggeber, otherwise a client id. */
	scope: string | null;
	overview: ArchiveOverview | null;
	overviewFailed: boolean;
	/** Count of files matching the active filters; null when no filter is active. */
	filteredCount: number | null;

	filterYear: number | null;
	filterKinds: string[];
	search: string;
	sort: ArchiveSort;
	viewMode: ArchiveViewMode;

	// grouped view
	jobGroups: ArchiveJobGroup[];
	/** True only on an initial load with nothing to show yet. */
	jobGroupsLoading: boolean;
	/** True during a filter refetch while the previous result stays on screen. */
	revalidating: boolean;
	jobGroupsLoadingMore: boolean;
	jobGroupsHasMore: boolean;
	expandedKeys: string[];
	expandedFiles: Record<string, ArchiveFileType[]>;
	expandedLoading: Record<string, boolean>;
	expandedHasMore: Record<string, boolean>;

	// flat view (search active or user-toggled)
	flatFiles: ArchiveFileType[];
	flatLoading: boolean;
	flatLoadingMore: boolean;
	flatHasMore: boolean;

	selectedFileIds: string[];
	selectAllCapReached: boolean;

	failed: boolean;
	requestId: number;
	listToken: number;
}

export interface ArchiveSlice extends ArchiveState {
	setArchiveScope: (scope: string | null) => Promise<void>;
	setFilterYear: (year: number | null) => void;
	toggleFilterKind: (kind: string) => void;
	setSearch: (term: string) => void;
	setSort: (sort: ArchiveSort) => void;
	setViewMode: (mode: ArchiveViewMode) => void;
	resetFilters: () => void;
	loadMoreJobGroups: () => Promise<void>;
	loadMoreGroupFiles: (group: ArchiveJobGroup) => Promise<void>;
	loadMoreFlatFiles: () => Promise<void>;
	toggleJobExpanded: (group: ArchiveJobGroup) => void;
	toggleFileSelected: (fileId: string) => void;
	selectFiles: (fileIds: string[], selected: boolean) => void;
	selectAllFiltered: () => Promise<void>;
	clearSelection: () => void;
}

const initialState: ArchiveState = {
	scope: null,
	overview: null,
	overviewFailed: false,
	filteredCount: null,
	filterYear: null,
	filterKinds: [],
	search: "",
	sort: "date_desc",
	viewMode: "flat",
	jobGroups: [],
	jobGroupsLoading: false,
	revalidating: false,
	jobGroupsLoadingMore: false,
	jobGroupsHasMore: false,
	expandedKeys: [],
	expandedFiles: {},
	expandedLoading: {},
	expandedHasMore: {},
	flatFiles: [],
	flatLoading: false,
	flatLoadingMore: false,
	flatHasMore: false,
	selectedFileIds: [],
	selectAllCapReached: false,
	failed: false,
	requestId: 0,
	listToken: 0,
};

let searchTimer: ReturnType<typeof setTimeout> | null = null;

export const isClientScope = (scope: string | null): scope is string =>
	scope !== null && scope !== NO_CLIENT_SCOPE;

const scopeArgs = (scope: string | null) => {
	if (scope === NO_CLIENT_SCOPE) return { p_no_client: true };
	if (scope) return { p_client_id: scope };
	return {};
};

/**
 * Search results are flat, and so is every organisation-wide scope, which cannot be grouped
 * without aggregating the whole organisation. Otherwise the view toggle decides.
 */
export const isFlatView = (state: {
	scope: string | null;
	search: string;
	viewMode: ArchiveViewMode;
}) =>
	!isClientScope(state.scope) ||
	state.search.trim() !== "" ||
	state.viewMode === "flat";

export const createArchiveStore: StateCreator<ArchiveSlice> = (set, get) => {
	const filterArgs = () => {
		const { filterYear, filterKinds, search } = get();
		const args: {
			p_year?: number;
			p_kinds?: string[];
			p_search?: string;
		} = {};
		if (filterYear !== null) args.p_year = filterYear;
		if (filterKinds.length) args.p_kinds = filterKinds;
		const trimmed = search.trim();
		if (trimmed) args.p_search = trimmed;
		return args;
	};

	const hasActiveFilter = () => {
		const { filterYear, filterKinds, search } = get();
		return (
			filterYear !== null ||
			filterKinds.length > 0 ||
			search.trim() !== ""
		);
	};

	/** A filter change invalidates the selection: it could hold files the new filter hides. */
	const clearedSelection = {
		selectedFileIds: [],
		selectAllCapReached: false,
	};

	/** Only a client scope is counted; counting an organisation resolves all of its files. */
	const loadFilteredCount = async (
		organizationId: string,
		listToken: number
	) => {
		const { scope } = get();
		if (!isClientScope(scope) || !hasActiveFilter()) {
			set({ filteredCount: null });
			return;
		}
		const { data, error } = await supabase.rpc("archive_file_count", {
			p_organization_id: organizationId,
			p_client_id: scope,
			...filterArgs(),
		});
		if (get().listToken !== listToken) return;
		if (error) {
			Logger.error(error, {}, "Error counting archive files");
			return;
		}
		set({ filteredCount: data ?? 0 });
	};

	/** Loads (or reloads/appends) the files of one expanded group, keyset-paged. */
	const loadGroupFiles = async (
		group: ArchiveJobGroup,
		append: boolean,
		listToken: number
	) => {
		const organizationId = useCentralStore.getState().organization?.id;
		const { scope } = get();
		if (!organizationId || !isClientScope(scope)) return;
		const key = groupKey(group);

		const existing = append ? get().expandedFiles[key] ?? [] : [];
		const last =
			append && existing.length ? existing[existing.length - 1] : null;

		set(
			produce((state) => {
				state.expandedLoading[key] = true;
			})
		);

		const { data, error } = await supabase.rpc("archive_job_files", {
			p_organization_id: organizationId,
			p_client_id: scope,
			p_job_id: group.resolved_job_id ?? undefined,
			p_no_job: group.resolved_job_id === null,
			...filterArgs(),
			p_after_date: last?.created_at ?? undefined,
			p_after_id: last?.id ?? undefined,
			p_limit: FILES_PAGE_SIZE,
		});

		if (get().listToken !== listToken) return;
		if (error) {
			Logger.error(error, {}, "Error loading archive job files");
			set(
				produce((state) => {
					state.expandedLoading[key] = false;
				})
			);
			showNotification({
				message: "Dateien konnten nicht geladen werden",
				type: "error",
			});
			return;
		}

		const page = data ?? [];
		set(
			produce((state) => {
				state.expandedFiles[key] = append
					? [...existing, ...page]
					: page;
				state.expandedLoading[key] = false;
				state.expandedHasMore[key] = page.length === FILES_PAGE_SIZE;
			})
		);
	};

	/**
	 * Grouped view: fetch groups, keeping the old list visible until the new one arrives.
	 * silent skips the dimmed revalidating state, for background refreshes.
	 */
	const reloadGrouped = async (silent = false) => {
		const organizationId = useCentralStore.getState().organization?.id;
		const { scope } = get();
		if (!organizationId || !isClientScope(scope)) return;

		const listToken = get().listToken + 1;
		const hadData = get().jobGroups.length > 0;
		const openKeys = get().expandedKeys;

		set({
			listToken,
			revalidating: hadData && !silent,
			jobGroupsLoading: !hadData,
			failed: false,
		});

		void loadFilteredCount(organizationId, listToken);

		const { data, error } = await supabase.rpc("archive_job_groups", {
			p_organization_id: organizationId,
			p_client_id: scope,
			...filterArgs(),
			p_limit: JOB_GROUPS_PAGE_SIZE,
		});

		if (get().listToken !== listToken) return;
		if (error) {
			Logger.error(error, {}, "Error loading archive job groups");
			set({ jobGroupsLoading: false, revalidating: false, failed: true });
			showNotification({
				message: "Archiv konnte nicht geladen werden",
				type: "error",
			});
			return;
		}

		const groups: ArchiveJobGroup[] = data ?? [];
		const present = new Set(groups.map((g) => groupKey(g)));
		const keptOpen = openKeys.filter((k) => present.has(k));

		set({
			jobGroups: groups,
			jobGroupsLoading: false,
			revalidating: false,
			jobGroupsHasMore: groups.length === JOB_GROUPS_PAGE_SIZE,
			expandedKeys: keptOpen,
			expandedFiles: {},
			expandedLoading: {},
			expandedHasMore: {},
		});

		// Re-fetch files for groups that stayed open, so an expanded group survives a filter
		// change instead of snapping shut.
		keptOpen.forEach((k) => {
			const group = groups.find((g) => groupKey(g) === k);
			if (group) void loadGroupFiles(group, false, listToken);
		});
	};

	/** Flat view: sorted, keyset-paged files. */
	const reloadFlat = async (silent = false) => {
		const organizationId = useCentralStore.getState().organization?.id;
		const { scope, sort } = get();
		if (!organizationId) return;

		const listToken = get().listToken + 1;
		const hadData = get().flatFiles.length > 0;
		set({
			listToken,
			revalidating: hadData && !silent,
			flatLoading: !hadData,
			failed: false,
		});

		void loadFilteredCount(organizationId, listToken);

		const { data, error } = await supabase.rpc("archive_flat_files", {
			p_organization_id: organizationId,
			...scopeArgs(scope),
			...filterArgs(),
			p_sort: sort,
			p_limit: FILES_PAGE_SIZE,
		});

		if (get().listToken !== listToken) return;
		if (error) {
			Logger.error(error, {}, "Error loading archive flat files");
			set({ flatLoading: false, revalidating: false, failed: true });
			showNotification({
				message: "Archiv konnte nicht geladen werden",
				type: "error",
			});
			return;
		}

		const page = data ?? [];
		set({
			flatFiles: page,
			flatLoading: false,
			revalidating: false,
			flatHasMore: page.length === FILES_PAGE_SIZE,
		});
	};

	const reload = (silent = false) =>
		isFlatView(get()) ? reloadFlat(silent) : reloadGrouped(silent);

	const loadOverview = async () => {
		const organizationId = useCentralStore.getState().organization?.id;
		const { scope, requestId } = get();
		if (!organizationId) return;

		const { data, error } = await supabase.rpc("archive_overview", {
			p_organization_id: organizationId,
			...scopeArgs(scope),
		});

		if (get().requestId !== requestId) return;
		if (error || !data) {
			Logger.error(error, {}, "Error loading archive overview");
			set({ overviewFailed: true });
			return;
		}

		const raw = data as unknown as {
			total: number | null;
			min_date: string | null;
			max_date: string | null;
			years: { year: number; count: number | null }[];
			kinds: { kind: string; count: number }[] | null;
		};
		set({
			overviewFailed: false,
			overview: {
				total: raw.total,
				minDate: raw.min_date,
				maxDate: raw.max_date,
				years: raw.years,
				kinds: raw.kinds,
			},
		});
	};

	return {
		...initialState,

		setArchiveScope: async (scope) => {
			if (searchTimer) clearTimeout(searchTimer);

			// Re-entering the same scope keeps list, filters and selection and refreshes quietly.
			if (scope === get().scope && get().overview) {
				await Promise.all([loadOverview(), reload(true)]);
				return;
			}

			// listToken carries over: a response still in flight for the previous scope must not
			// match the first request of the new one.
			const requestId = get().requestId + 1;
			const flat = isFlatView({ ...initialState, scope });
			set({
				...initialState,
				scope,
				requestId,
				listToken: get().listToken,
				jobGroupsLoading: !flat,
				flatLoading: flat,
			});

			await Promise.all([loadOverview(), reload()]);
		},

		setFilterYear: (year) => {
			set({
				filterYear: get().filterYear === year ? null : year,
				...clearedSelection,
			});
			void reload();
		},

		toggleFilterKind: (kind) => {
			set(
				produce((state) => {
					const index = state.filterKinds.indexOf(kind);
					if (index === -1) state.filterKinds.push(kind);
					else state.filterKinds.splice(index, 1);
					state.selectedFileIds = [];
					state.selectAllCapReached = false;
				})
			);
			void reload();
		},

		setSearch: (term) => {
			const wasFlat = isFlatView(get());
			set({ search: term, ...clearedSelection });
			const nowFlat = isFlatView(get());
			if (searchTimer) clearTimeout(searchTimer);
			// Switching between grouped and flat should feel immediate; typing within a mode
			// is debounced.
			if (wasFlat !== nowFlat) void reload();
			else
				searchTimer = setTimeout(
					() => void reload(),
					SEARCH_DEBOUNCE_MS
				);
		},

		setSort: (sort) => {
			set({ sort });
			if (isFlatView(get())) void reloadFlat();
		},

		setViewMode: (mode) => {
			set({ viewMode: mode });
			void reload();
		},

		resetFilters: () => {
			if (searchTimer) clearTimeout(searchTimer);
			set({
				filterYear: null,
				filterKinds: [],
				search: "",
				sort: "date_desc",
				viewMode: "flat",
				...clearedSelection,
			});
			void reload();
		},

		loadMoreJobGroups: async () => {
			const organizationId = useCentralStore.getState().organization?.id;
			const { scope, jobGroups, jobGroupsLoadingMore, listToken } = get();
			if (
				!organizationId ||
				!isClientScope(scope) ||
				jobGroupsLoadingMore ||
				jobGroups.length === 0
			)
				return;

			const last = jobGroups[jobGroups.length - 1];
			set({ jobGroupsLoadingMore: true });

			const { data, error } = await supabase.rpc("archive_job_groups", {
				p_organization_id: organizationId,
				p_client_id: scope,
				...filterArgs(),
				p_after_date: last.latest_created_at,
				p_after_key: last.resolved_job_id ?? -1,
				p_limit: JOB_GROUPS_PAGE_SIZE,
			});

			if (get().listToken !== listToken) return;
			if (error) {
				Logger.error(
					error,
					{},
					"Error loading more archive job groups"
				);
				set({ jobGroupsLoadingMore: false });
				showNotification({
					message: "Weitere Aufträge konnten nicht geladen werden",
					type: "error",
				});
				return;
			}

			const page = data ?? [];
			set(
				produce((state) => {
					state.jobGroups.push(...page);
					state.jobGroupsLoadingMore = false;
					state.jobGroupsHasMore =
						page.length === JOB_GROUPS_PAGE_SIZE;
				})
			);
		},

		loadMoreGroupFiles: async (group) => {
			await loadGroupFiles(group, true, get().listToken);
		},

		loadMoreFlatFiles: async () => {
			const organizationId = useCentralStore.getState().organization?.id;
			const { scope, flatFiles, flatLoadingMore, sort, listToken } =
				get();
			if (!organizationId || flatLoadingMore || flatFiles.length === 0)
				return;

			const last = flatFiles[flatFiles.length - 1];
			set({ flatLoadingMore: true });

			const { data, error } = await supabase.rpc("archive_flat_files", {
				p_organization_id: organizationId,
				...scopeArgs(scope),
				...filterArgs(),
				p_sort: sort,
				p_after_date: last.created_at ?? undefined,
				p_after_name: last.file_name ?? undefined,
				p_after_id: last.id ?? undefined,
				p_limit: FILES_PAGE_SIZE,
			});

			if (get().listToken !== listToken) return;
			if (error) {
				Logger.error(error, {}, "Error loading more archive files");
				set({ flatLoadingMore: false });
				showNotification({
					message: "Weitere Dateien konnten nicht geladen werden",
					type: "error",
				});
				return;
			}

			const page = data ?? [];
			set(
				produce((state) => {
					state.flatFiles.push(...page);
					state.flatLoadingMore = false;
					state.flatHasMore = page.length === FILES_PAGE_SIZE;
				})
			);
		},

		toggleJobExpanded: (group) => {
			const key = groupKey(group);
			const { expandedKeys, expandedFiles, listToken } = get();

			if (expandedKeys.includes(key)) {
				set(
					produce((state) => {
						state.expandedKeys = state.expandedKeys.filter(
							(k: string) => k !== key
						);
					})
				);
				return;
			}

			set(
				produce((state) => {
					state.expandedKeys.push(key);
				})
			);

			if (!expandedFiles[key])
				void loadGroupFiles(group, false, listToken);
		},

		toggleFileSelected: (fileId) => {
			set(
				produce((state) => {
					const index = state.selectedFileIds.indexOf(fileId);
					if (index === -1) state.selectedFileIds.push(fileId);
					else state.selectedFileIds.splice(index, 1);
					state.selectAllCapReached = false;
				})
			);
		},

		selectFiles: (fileIds, selected) => {
			set(
				produce((state) => {
					if (selected) {
						const next = new Set(state.selectedFileIds);
						fileIds.forEach((id) => next.add(id));
						state.selectedFileIds = Array.from(next);
					} else {
						const remove = new Set(fileIds);
						state.selectedFileIds = state.selectedFileIds.filter(
							(id: string) => !remove.has(id)
						);
					}
					state.selectAllCapReached = false;
				})
			);
		},

		selectAllFiltered: async () => {
			const organizationId = useCentralStore.getState().organization?.id;
			const { scope, listToken } = get();
			if (!organizationId) return;

			const { data, error } = await supabase.rpc("archive_file_ids", {
				p_organization_id: organizationId,
				...scopeArgs(scope),
				...filterArgs(),
				p_limit: SELECT_ALL_CAP,
			});
			// A scope switch or filter change happened while this was in flight - do not
			// write the previous context's ids onto the current selection.
			if (get().scope !== scope || get().listToken !== listToken) return;
			if (error) {
				Logger.error(error, {}, "Error selecting all archive files");
				showNotification({
					message: "Dateien konnten nicht ausgewählt werden",
					type: "error",
				});
				return;
			}
			const ids = (data ?? []).map((row) => row.id);
			set({
				selectedFileIds: ids,
				selectAllCapReached: ids.length >= SELECT_ALL_CAP,
			});
		},

		clearSelection: () =>
			set({ selectedFileIds: [], selectAllCapReached: false }),
	};
};
